import React from 'react';
import { MapPin, Phone, Mail, MessageCircle, Facebook, Instagram, Clock } from 'lucide-react';

const ContactPage = ({ galleryInfo, siteSettings, branches = [] }) => {

    // Map URLs
    const unawatunaMapUrl = "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3967.8730512656693!2d80.24545237485295!3d6.012163593973125!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3ae173fa8728a4e3%3A0x8549521e9682008c!2sKolinsky%20Art%20Gallery!5e0!3m2!1sen!2slk!4v1761584050530!5m2!1sen!2slk";
    const mirissaMapUrl = "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3968.3295763877013!2d80.45318297485242!3d5.949242894035286!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3ae13fd8a3087f63%3A0x3af50d25d0290abe!2sKolinsky%20Art%20Gallery!5e0!3m2!1sen!2slk!4v1761584734786!5m2!1sen!2slk";
    const ahangamaMapUrl = "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d126979.22296683984!2d80.26949058104401!3d5.980867846013862!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3ae11340de0d59dd%3A0xc93cf7f75a1899ca!2sKolinsky%20Art%20Gallery!5e0!3m2!1sen!2slk!4v1762677669696!5m2!1sen!2slk";
    
    const displayPhoneNumber = "+94 77 364 3253";
    const telLinkPhoneNumber = "+94773643253";
    const displayWhatsAppNumber = "+94 77 364 3253";
    const whatsappLinkNumber = "94773643253";
    const openingHours = "8:00 AM - 11:00 PM Daily";

    // Contact Info Item Component
    const ContactInfoItem = ({ href, icon: Icon, label, value, hoverColorClass }) => (
        <a
          href={href}
          target={href.startsWith('http') || href.startsWith('mailto') ? '_blank' : '_self'}
          rel="noopener noreferrer"
          className={`flex items-center gap-3 sm:gap-4 p-3 sm:p-5 rounded-lg sm:rounded-xl border-2 border-gray-200 hover:border-orange-500 hover:bg-orange-50 transition-all group`}
        >
          <div className={`w-10 sm:w-12 h-10 sm:h-12 ${hoverColorClass.replace('hover:bg-', 'bg-').replace('-50', '-100')} rounded-full flex items-center justify-center group-hover:${hoverColorClass} transition-colors flex-shrink-0`}>
            <Icon className={`w-5 sm:w-6 h-5 sm:h-6 ${hoverColorClass.replace('hover:bg-orange-50', 'text-orange-700').replace('hover:bg-green-50', 'text-green-700').replace('hover:bg-blue-50', 'text-blue-700')}`} />
          </div>
          <div className="min-w-0">
            <p className="text-xs sm:text-sm text-gray-600 mb-0.5 sm:mb-1">{label}</p>
            <p className="font-semibold text-xs sm:text-base text-gray-800 break-words truncate sm:break-normal">{value}</p>
          </div>
        </a>
    );

    return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="relative h-[30vh] sm:h-[35vh] md:h-[40vh] min-h-[250px] flex items-center justify-center overflow-hidden bg-gray-800">
        <div className="absolute inset-0">
          <img
            src="https://sgkolinskyweb.blob.core.windows.net/$web/hero-contact.jpg"
            alt="Scenic view representing Kolinsky Art Gallery locations"
            className="w-full h-full object-cover"
            onError={(e) => { e.target.onerror = null; e.target.src="https://placehold.co/1920x600/gray/white?text=Error"; }}
          />
          <div className="absolute inset-0 bg-gray-900/60" />
        </div>
        <div className="relative z-10 text-center text-white px-4">
          <h1 className="text-2xl sm:text-3xl md:text-5xl lg:text-6xl font-serif font-bold mb-2 sm:mb-3 md:mb-4">Visit Us</h1>
          <p className="text-xs sm:text-base md:text-lg lg:text-xl font-light">One Main Gallery & Two Branch Locations on Sri Lanka's Coast</p>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12 md:py-16">
        
        {/* MAIN GALLERY - UNAWATUNA */}
        <div className="mb-8 sm:mb-12 md:mb-16">
          <h2 className="text-xl sm:text-2xl md:text-3xl font-serif font-bold text-gray-800 mb-4 sm:mb-6 text-center">Main Gallery</h2>
          <div className="bg-white rounded-lg sm:rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
            <div className="h-32 sm:h-40 md:h-48 bg-gradient-to-br from-orange-500 to-orange-700 flex items-center justify-center">
              <div className="text-center text-white">
                <MapPin className="w-8 sm:w-10 md:w-12 h-8 sm:h-10 md:h-12 mx-auto mb-1 sm:mb-2" />
                <h3 className="text-lg sm:text-xl md:text-2xl font-serif font-bold">Unawatuna</h3>
              </div>
            </div>
            <div className="p-4 sm:p-6 md:p-8">
              <h4 className="font-semibold text-sm sm:text-base md:text-lg text-gray-800 mb-1 sm:mb-2">Kolinsky Art Gallery - Unawatuna</h4>
              <p className="text-xs sm:text-sm md:text-base text-gray-600 mb-4 sm:mb-6">{siteSettings && siteSettings.addressUnawatuna ? siteSettings.addressUnawatuna : 'Unawatuna, Sri Lanka'}</p>
              <div className="rounded-lg overflow-hidden border w-full h-48 sm:h-64 md:h-80">
                <iframe
                  src={unawatunaMapUrl}
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Unawatuna Main Gallery Location"
                />
              </div>
            </div>
          </div>
        </div>

        {/* BRANCH GALLERIES */}
        <div className="mb-8 sm:mb-12 md:mb-16">
          <h2 className="text-xl sm:text-2xl md:text-3xl font-serif font-bold text-gray-800 mb-4 sm:mb-6 text-center">Branch Galleries</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 md:gap-8 lg:gap-12">
            
            {/* Branch - Mirissa */}
            <div className="bg-white rounded-lg sm:rounded-2xl shadow-lg border border-gray-100 overflow-hidden flex flex-col">
              <div className="h-32 sm:h-40 md:h-48 bg-gradient-to-br from-gray-600 to-gray-800 flex items-center justify-center">
                <div className="text-center text-white">
                  <MapPin className="w-8 sm:w-10 md:w-12 h-8 sm:h-10 md:h-12 mx-auto mb-1 sm:mb-2" />
                  <h3 className="text-lg sm:text-xl md:text-2xl font-serif font-bold">Mirissa Branch</h3>
                </div>
              </div>
              <div className="p-4 sm:p-6 md:p-8 flex-grow flex flex-col">
                <h4 className="font-semibold text-sm sm:text-base md:text-lg text-gray-800 mb-1 sm:mb-2">Mirissa</h4>
                <p className="text-xs sm:text-sm md:text-base text-gray-600 mb-4 sm:mb-6 flex-grow">{siteSettings && siteSettings.addressMirissa ? siteSettings.addressMirissa : 'Mirissa, Sri Lanka'}</p>
                <div className="rounded-lg overflow-hidden border w-full h-48 sm:h-64 md:h-80">
                  <iframe
                    src={mirissaMapUrl}
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    allowFullScreen=""
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="Mirissa Branch Location"
                  />
                </div>
              </div>
            </div>

            {/* Branch - Ahangama */}
            <div className="bg-white rounded-lg sm:rounded-2xl shadow-lg border border-gray-100 overflow-hidden flex flex-col">
              <div className="h-32 sm:h-40 md:h-48 bg-gradient-to-br from-teal-600 to-teal-800 flex items-center justify-center">
                <div className="text-center text-white">
                  <MapPin className="w-8 sm:w-10 md:w-12 h-8 sm:h-10 md:h-12 mx-auto mb-1 sm:mb-2" />
                  <h3 className="text-lg sm:text-xl md:text-2xl font-serif font-bold">Ahangama Branch</h3>
                </div>
              </div>
              <div className="p-4 sm:p-6 md:p-8 flex-grow flex flex-col">
                <h4 className="font-semibold text-sm sm:text-base md:text-lg text-gray-800 mb-1 sm:mb-2">Ahangama</h4>
                <p className="text-xs sm:text-sm md:text-base text-gray-600 mb-4 sm:mb-6 flex-grow">Ahangama, Sri Lanka</p>
                <div className="rounded-lg overflow-hidden border w-full h-48 sm:h-64 md:h-80">
                  <iframe
                    src={ahangamaMapUrl}
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    allowFullScreen=""
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="Ahangama Branch Location"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Contact Information */}
        <div className="bg-white rounded-lg sm:rounded-2xl shadow-lg border border-gray-100 p-5 sm:p-8 md:p-12 max-w-5xl mx-auto">
          <h2 className="text-xl sm:text-2xl md:text-3xl font-serif font-bold text-gray-800 mb-6 sm:mb-8 text-center">
            Get in Touch
          </h2>

          <>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 md:gap-6 mb-6 sm:mb-8">
                {/* Phone */}
                <ContactInfoItem
                    href={`tel:${telLinkPhoneNumber}`}
                    icon={Phone}
                    label="Call Us"
                    value={displayPhoneNumber}
                    hoverColorClass="hover:bg-orange-50"
                 />

                {/* Email */}
                <ContactInfoItem
                    href={`mailto:${siteSettings && siteSettings.emailAddress ? siteSettings.emailAddress : 'info@example.com'}`}
                    icon={Mail}
                    label="Email Us"
                    value={siteSettings && siteSettings.emailAddress ? siteSettings.emailAddress : 'info@example.com'}
                    hoverColorClass="hover:bg-orange-50"
                 />

                {/* WhatsApp */}
                <ContactInfoItem
                    href={`https://wa.me/${whatsappLinkNumber}`}
                    icon={MessageCircle}
                    label="WhatsApp"
                    value={displayWhatsAppNumber}
                    hoverColorClass="hover:bg-green-50"
                 />

                {/* Hours */}
                <div className="flex items-center gap-3 sm:gap-4 p-3 sm:p-5 rounded-lg sm:rounded-xl border-2 border-gray-200">
                  <div className={`w-10 sm:w-12 h-10 sm:h-12 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0`}>
                    <Clock className={`w-5 sm:w-6 h-5 sm:h-6 text-blue-700`} />
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs sm:text-sm text-gray-600 mb-0.5 sm:mb-1">Opening Hours</p>
                    <p className="font-semibold text-xs sm:text-base text-gray-800 break-words">{openingHours}</p>
                  </div>
                </div>
              </div>

              {/* Social Media */}
              {(siteSettings && (siteSettings.facebookLink || siteSettings.instagramLink)) && (
                <div className="border-t border-gray-200 pt-6 sm:pt-8">
                  <p className="text-center text-xs sm:text-base text-gray-600 mb-4 sm:mb-6 font-medium">Connect With Us</p>
                  <div className="flex justify-center gap-3 sm:gap-4">
                    {siteSettings.facebookLink && (
                      <a
                        href={siteSettings.facebookLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-10 sm:w-12 h-10 sm:h-12 bg-blue-600 hover:bg-blue-700 text-white rounded-full flex items-center justify-center transition-colors flex-shrink-0"
                        aria-label="Facebook"
                      >
                        <Facebook className="w-5 sm:w-6 h-5 sm:h-6" />
                      </a>
                    )}
                    {siteSettings.instagramLink && (
                      <a
                        href={siteSettings.instagramLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-10 sm:w-12 h-10 sm:h-12 bg-gradient-to-br from-purple-600 via-pink-600 to-orange-500 hover:opacity-90 text-white rounded-full flex items-center justify-center transition-opacity flex-shrink-0"
                        aria-label="Instagram"
                      >
                        <Instagram className="w-5 sm:w-6 h-5 sm:h-6" />
                      </a>
                    )}
                  </div>
                </div>
              )}
            </>

        </div>
      </div>
    </div>
  );
};

export default ContactPage;